import { createInstance } from '@jonkoops/matomo-tracker-react';
import { AnalyticsEvent, AnalyticsProvider, PageViewEvent } from '../../types/analytics.types';
import { ConfigService } from '@/configuration/services/configService';

export class MatomoAnalyticsProvider implements AnalyticsProvider {
    public readonly name = 'Matomo';
    private instance: ReturnType<typeof createInstance> | null = null;
    private enabled: boolean = false;

    constructor() {
        this.initializeMatomo();
    }

    private initializeMatomo(): void {
        const config = ConfigService.get();
        const matomoConfig = config.application.analytics?.matomo;

        if (matomoConfig?.enabled) {
            this.enabled = true;
            this.instance = createInstance({
                disabled: false,
                urlBase: matomoConfig.urlBase ?? '',
                siteId: matomoConfig.siteId ?? 0,
                userId: matomoConfig.userId,
                trackerUrl: matomoConfig.trackerUrl,
                srcUrl: matomoConfig.srcUrl,
                heartBeat: matomoConfig.heartBeat
                    ? {
                          active: matomoConfig.heartBeat.active ?? true,
                          seconds: matomoConfig.heartBeat.seconds,
                      }
                    : undefined,
                linkTracking: matomoConfig.linkTracking,
                configurations: matomoConfig.configurations ?? {},
            });
        }
    }

    trackPageView(event: PageViewEvent): void {
        if (!this.instance) return;

        this.instance.trackPageView({
            documentTitle: event.documentTitle,
            href: event.href,
            customDimensions: event.customDimensions,
        });
    }

    trackEvent(event: AnalyticsEvent): void {
        if (!this.instance) return;

        this.instance.trackEvent({
            category: event.category,
            action: event.action,
            name: event.name,
            value: event.value,
            customDimensions: event.customDimensions,
        });
    }

    deleteCustomDimensions(ids: number[]): void {
        if (!this.instance) return;

        ids.forEach((id) => {
            this.instance?.pushInstruction('deleteCustomDimension', id);
        });
    }

    setUserId(userId: string): void {
        if (!this.instance) return;

        this.instance.pushInstruction('setUserId', userId);
    }

    resetUserId(): void {
        if (!this.instance) return;

        this.instance.pushInstruction('resetUserId');
        this.instance.pushInstruction('appendToTrackingUrl', 'new_visit=1');
        this.instance.trackPageView({
            documentTitle: document.title,
            href: window.location.href,
        });
        this.instance.pushInstruction('appendToTrackingUrl', '');
    }

    isEnabled(): boolean {
        return this.enabled && this.instance !== null;
    }

    getInstance(): ReturnType<typeof createInstance> | null {
        return this.instance;
    }
}
